import React from 'react';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import Velocity from 'velocity-animate';

import { connect } from 'react-redux';

import Pricing from '@component/pricing';

import I18n from '@module/i18n';
import Analytics from '@module/analytics';
import SplashpageHelper from '@app/helper/splashpage';
import { FREE_ACCOUNT, PLUS_ACCOUNT } from '@constant/subscriptions';
import { FF_HEADSPACE_PROMOTION } from '@constant/experiments';

import Actions from '@app/action';
import LaunchDarkly from '@module/launchdarkly';

class FreeAccount extends React.PureComponent {
  componentDidMount() {
    window.addEventListener('keydown', this.handleEscape);

    SplashpageHelper.trackShow();

    const { session, getSubscriptionPricing } = this.props;
    getSubscriptionPricing({
      country_code: session.region.country || '',
      vat_number: '',
      coupon_code: '',
      plan_tier: '',
    });
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleEscape);
  }

  onExit(component) {
    Velocity(
      component,
      {
        scale: '1.3',
        opacity: 0,
      },
      { duration: 300 }
    );
  }

  handleEscape = (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    SplashpageHelper.trackFreeFunnel({
      action: 'click_i_just_want_to_send_my_files',
    });

    this.props.onEscape();
  };

  handleFreeAccountClick = (event) => {
    // Stop Triggering GA tracker in handleClick()
    event.stopPropagation();

    Analytics.trackStructEvent({
      category: 'splashscreen',
      action: 'click_create_an_account',
    });

    this.props.onPlusClick('Splashpage_freeaccount', '/sign-up', 'free');
  };

  handleFreeClick = (event) => {
    // Stop Triggering GA tracker in handleClick()
    event.stopPropagation();

    SplashpageHelper.trackFreeFunnel({
      action: 'click_i_just_want_to_send_my_files',
    });

    this.props.onFreeClick();
  };

  handlePlusClick = (event) => {
    // Stop Triggering GA tracker in handleClick()
    event.stopPropagation();

    SplashpageHelper.trackPlusFunnel();

    this.props.onPlusClick('Splashpage_freeaccount', '/sign-up', 'plus');
  };

  handleHeadspaceClick = (event) => {
    // Stop Triggering GA tracker in handleClick()
    event.stopPropagation();

    SplashpageHelper.trackPlusFunnel();

    this.props.onPlusClick('Splashpage_freeaccount', '/pro');
  };

  handleClick = () => {
    SplashpageHelper.trackFreeFunnel({
      action: 'click_i_just_want_to_send_my_files',
    });

    this.props.onClick();
  };

  renderPricing() {
    const { session, ui } = this.props;

    if (!ui.subscriptionPricing) {
      return <Pricing currency={session.region.currency} total={12} />;
    }

    const currency = ui.subscriptionPricing.month.currency;
    const total = ui.subscriptionPricing.month.total;
    return <Pricing currency={currency} total={total} />;
  }

  render() {
    return (
      <Transition
        in={this.props.in}
        timeout={{ in: 0, exit: 300 }}
        unmountOnExit={true}
        onExit={this.onExit}
      >
        <div
          className="splashpage free-account-tiers"
          onClick={this.handleClick}
        >
          <div>
            <div className="splashpage__background" />

            <div className="content">
              <h1 className="content__heading">
                {I18n.t('splash.heading.line_1_free')}
                <br />
                {I18n.t('splash.heading.line_2_free')}
              </h1>

              <div className="tiers__list">
                <div className="tier">
                  <div className="tier__content">
                    <h2 className="tier__title">WeTransfer Account</h2>
                    <h3 className="tier__subtitle">
                      {I18n.t('page.signin.account_subtitle')}
                    </h3>
                    <ul className="tier__features">
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.account_item_1', {
                          size_limit: FREE_ACCOUNT.TRANSFER_LIMIT,
                        })}
                      </li>
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.account_item_2')}
                      </li>
                    </ul>
                  </div>
                  <button
                    className="button button--enabled"
                    onClick={this.handleFreeAccountClick}
                  >
                    {I18n.t('page.signin.account_cta')}
                  </button>
                </div>

                <div className="tier">
                  <div className="tier__content">
                    <h2 className="tier__title">WeTransfer Pro</h2>
                    <h3 className="tier__subtitle">
                      <div className="tier__price">
                        {this.renderPricing()}
                        <span className="tier__period">
                          {I18n.t('page.signin.plus_month')}
                        </span>
                      </div>
                    </h3>
                    <ul className="tier__features">
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.plus_item_1', {
                          size_limit: PLUS_ACCOUNT.TRANSFER_LIMIT,
                        })}
                      </li>
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.plus_item_2')}
                      </li>
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.plus_item_3', {
                          storage_limit: PLUS_ACCOUNT.STORAGE_LIMIT,
                        })}
                      </li>
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.plus_item_4')}
                      </li>
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.plus_item_5')}
                      </li>
                      <li className="tier__feature-item">
                        {I18n.t('page.signin.plus_item_6')}
                      </li>
                      {LaunchDarkly.getVariant(FF_HEADSPACE_PROMOTION) && (
                        <li className="tier__feature-item">
                          <span
                            onClick={this.handleHeadspaceClick}
                            dangerouslySetInnerHTML={{
                              __html: I18n.t(
                                'splash.plan.plus.feature_headspace'
                              ),
                            }}
                          />
                        </li>
                      )}
                    </ul>
                  </div>
                  <button
                    className="button button--enabled button--inline"
                    onClick={this.handlePlusClick}
                  >
                    {I18n.t('page.signin.plus_cta')}
                  </button>
                </div>
              </div>

              <div className="free-button-wrapper">
                <button
                  className="button button--enabled button--inline free-button"
                  onClick={this.handleFreeClick}
                >
                  {I18n.t('splash.footer.send_files')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    );
  }
}

FreeAccount.propTypes = {
  in: PropTypes.bool,
  onEscape: PropTypes.func.isRequired,
  onFreeClick: PropTypes.func.isRequired,
  onPlusClick: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  getSubscriptionPricing: PropTypes.func.isRequired,
  ui: PropTypes.object.isRequired,
  session: PropTypes.object.isRequired,
};

const mapStateToProps = ({ ui, session }) => ({
  ui,
  session,
});

const mapDispatchToProps = {
  getSubscriptionPricing: Actions.UI.getSubscriptionPricing,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(FreeAccount);
